"use client";

import React, { useState, useEffect } from "react";
import { SafeImage } from "@/src/components/SafeImage";

interface AboutMakerProps {
  storeName?: string;
  description?: string;
  founderQuote?: string;
  banner?: string;
  makerUrl?: string;
  websiteLink?: string;
  heading?: string;
  mainTitle?: string;
  ctaLabel?: string;
}

export const AboutMaker: React.FC<AboutMakerProps> = ({
  storeName,
  description,
  founderQuote = "Every product tells the story of a single maker.",
  banner,
  makerUrl,
  websiteLink,
  heading,
  mainTitle,
  ctaLabel,
}) => {
  const [open, setOpen] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const storyText =
    description ||
    "What started as a small experiment has grown into a beloved local brand. Every product is crafted with intention — sourced carefully, made in small batches, and signed with the maker's name. No shortcuts. No mass scale. Just products the way they should be made.";

  return (
    <>
      <section className="mt-[clamp(40px,5vw,64px)]">
        <div
          className="relative overflow-hidden rounded-[clamp(16px,2vw,24px)] text-white"
          style={{ background: "linear-gradient(120deg,#06223E,#0E3A66 75%)" }}
        >
          {/* Rising-sun arc motif */}
          <span
            className="pointer-events-none absolute"
            style={{ right: -60, top: -80, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle at 50% 100%, rgba(242,98,80,0.5), transparent 65%)" }}
          />
          <div className="relative z-10 flex flex-col items-center gap-5 p-[clamp(22px,3.5vw,38px)] sm:flex-row sm:gap-7">
            {/* Maker image / monogram */}
            <div
              className="relative h-[120px] w-[120px] shrink-0 overflow-hidden rounded-2xl sm:h-[136px] sm:w-[136px]"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)" }}
            >
              {(makerUrl || banner) ? (
                <SafeImage
                  src={makerUrl || banner}
                  alt={storeName ?? "Maker"}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center" style={{ fontFamily: "var(--font-display)", fontSize: 52, color: "rgba(255,255,255,0.55)" }}>
                  {(storeName ?? "S").charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Text */}
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <div
                className="mb-2.5 inline-flex items-center gap-2.5 font-bold uppercase tracking-[0.18em]"
                style={{ fontSize: 11, color: "var(--gold)" }}
              >
                <span style={{ width: 26, height: 1, background: "var(--gold)", display: "inline-block" }} />
                {heading || "Meet the maker"}
              </div>
              <h2 className="leading-[1.12]" style={{ fontFamily: "var(--font-display)", fontWeight: 500, fontSize: "clamp(22px,3vw,32px)", letterSpacing: "-0.01em" }}>
                {mainTitle || "A story rooted in passion, brewed with care."}
              </h2>
              <p className="mx-auto mt-2 max-w-[460px] text-[13.5px] leading-relaxed sm:mx-0" style={{ color: "rgba(255,255,255,0.72)" }}>
                {storeName ? `${storeName} — ` : ""}{founderQuote}
              </p>
            </div>

            {/* CTA */}
            <button
              onClick={() => setOpen(true)}
              className="inline-flex shrink-0 items-center gap-2 rounded-full px-6 py-3 text-[14px] font-bold text-white transition hover:-translate-y-0.5"
              style={{ background: "var(--coral)", boxShadow: "0 10px 26px -10px rgba(242,98,80,0.7)" }}
            >
              {ctaLabel || "Read our story"}
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>
      </section>

      {/* Story Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          style={{ background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)" }}
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-2xl overflow-hidden rounded-t-[24px] sm:rounded-[24px] bg-white"
            style={{ maxHeight: "90vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}
          >
            {/* Banner image */}
            {banner ? (
              <div className="relative h-[220px] w-full overflow-hidden">
                <SafeImage
                  src={banner}
                  alt={storeName ?? "Store"}
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.45) 0%, transparent 60%)" }} />
                {storeName && (
                  <h2
                    className="absolute bottom-4 left-6 font-bold text-white"
                    style={{ fontSize: "clamp(18px,3vw,24px)", textShadow: "0 1px 6px rgba(0,0,0,0.5)" }}
                  >
                    {storeName}
                  </h2>
                )}
              </div>
            ) : (
              <div
                className="flex h-[140px] w-full items-end px-6 pb-4"
                style={{ background: "linear-gradient(135deg, #2A3527, var(--chutney-green))" }}
              >
                {storeName && (
                  <h2 className="font-bold text-white" style={{ fontSize: "clamp(18px,3vw,24px)" }}>
                    {storeName}
                  </h2>
                )}
              </div>
            )}

            {/* Content */}
            <div className="px-6 py-6">
              {/* Label */}
              <div
                className="mb-3 flex items-center gap-2 font-bold uppercase tracking-[2px]"
                style={{ fontSize: 10, color: "var(--chutney-green)" }}
              >
                <span style={{ width: 24, height: 1, background: "var(--chutney-green)", display: "inline-block" }} />
                Our Story
              </div>

              <p
                className="leading-relaxed whitespace-pre-line"
                style={{ fontSize: "clamp(14px,1.7vw,16px)", color: "var(--techno-black-70)" }}
              >
                {storyText}
              </p>

              {/* Website link */}
              {websiteLink && (
                <a
                  href={websiteLink.startsWith("http") ? websiteLink : `https://${websiteLink}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition"
                  style={{ background: "var(--chutney-green)", color: "#fff" }}
                  onMouseEnter={e => (e.currentTarget.style.opacity = "0.85")}
                  onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
                >
                  {/* Globe icon */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="2" y1="12" x2="22" y2="12" />
                    <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20" />
                  </svg>
                  Visit our website
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
                  </svg>
                </a>
              )}
            </div>

            {/* Close button */}
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full transition"
              style={{ background: "rgba(0,0,0,0.35)", color: "#fff" }}
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
